import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Paper,
  Button,
  styled,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';

const MONTH_NAMES_FULL = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const MONTH_NAMES_SHORT = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];

const MonthCell = styled(Button, {
  shouldForwardProp: (prop) => !['isSelected', 'isCurrent'].includes(prop as string),
})<{ isSelected?: boolean; isCurrent?: boolean }>(({ theme, isSelected, isCurrent }) => ({
  flex: '1 0 30%',
  margin: theme.spacing(0.5),
  padding: theme.spacing(1, 0.5),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  minWidth: 0,
  fontWeight: isSelected || isCurrent ? 600 : 400,
  fontSize: '0.8rem',
  color: isSelected
    ? theme.palette.common.white
    : isCurrent
      ? theme.palette.primary.main
      : theme.palette.text.primary,
  backgroundColor: isSelected
    ? theme.palette.primary.main
    : isCurrent
      ? theme.palette.primary.light + '20'
      : theme.palette.action.hover,
  '&:hover': {
    backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.selected,
  },
}));

const YearCell = styled(MonthCell)({});

const DateButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: theme.spacing(0.5, 1.5),
  minWidth: 100,
  fontWeight: 500,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(0.5),
  },
}));

interface BaseProps {
  year: number;
  disabled?: boolean;
  minYear?: number;
  maxYear?: number;
}

interface MonthModeProps extends BaseProps {
  mode?: 'month';
  month: number;
  onChange: (year: number, month: number) => void;
  /** Блокировка отдельных месяцев (например, без утверждённого плана) */
  isMonthDisabled?: (year: number, month: number) => boolean;
}

interface YearModeProps extends BaseProps {
  mode: 'year';
  month?: never;
  onChange: (year: number) => void;
}

export type MonthNavigatorProps = MonthModeProps | YearModeProps;

type ViewType = 'months' | 'years';

export function MonthNavigator(props: MonthNavigatorProps) {
  const {
    year,
    disabled = false,
    minYear = new Date().getFullYear() - 5,
    maxYear = new Date().getFullYear() + 2,
  } = props;

  const isYearMode = props.mode === 'year';
  const month = isYearMode ? 1 : props.month;
  const isMonthDisabled = !isYearMode ? props.isMonthDisabled : undefined;

  /** Ближайший доступный месяц в заданном направлении (с учётом isMonthDisabled и границ лет) */
  const findAdjacentMonth = useCallback(
    (direction: -1 | 1): { year: number; month: number } | null => {
      let y = year;
      let m = month;
      for (;;) {
        m += direction;
        if (m === 0) {
          m = 12;
          y -= 1;
        } else if (m === 13) {
          m = 1;
          y += 1;
        }
        if (y < minYear || y > maxYear) return null;
        if (!isMonthDisabled || !isMonthDisabled(y, m)) return { year: y, month: m };
      }
    },
    [year, month, minYear, maxYear, isMonthDisabled]
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentView, setCurrentView] = useState<ViewType>(isYearMode ? 'years' : 'months');
  const [viewYear, setViewYear] = useState(year);
  const [yearGroupStart, setYearGroupStart] = useState(() => Math.floor(year / 12) * 12);

  const handlePrev = () => {
    if (disabled) return;
    if (isYearMode) {
      if (year > minYear) {
        (props.onChange as (year: number) => void)(year - 1);
      }
    } else {
      const target = findAdjacentMonth(-1);
      if (target) {
        (props.onChange as (year: number, month: number) => void)(target.year, target.month);
      }
    }
  };

  const handleNext = () => {
    if (disabled) return;
    if (isYearMode) {
      if (year < maxYear) {
        (props.onChange as (year: number) => void)(year + 1);
      }
    } else {
      const target = findAdjacentMonth(1);
      if (target) {
        (props.onChange as (year: number, month: number) => void)(target.year, target.month);
      }
    }
  };

  const handleOpenPicker = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setCurrentView(isYearMode ? 'years' : 'months');
    setViewYear(year);
    setYearGroupStart(Math.floor(year / 12) * 12);
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
  };

  const handleYearSelect = useCallback((selectedYear: number) => {
    if (isYearMode) {
      (props.onChange as (year: number) => void)(selectedYear);
      handleClosePicker();
    } else {
      setViewYear(selectedYear);
      setCurrentView('months');
    }
  }, [isYearMode, props.onChange]);

  const handleMonthSelect = useCallback((selectedMonth: number) => {
    if (!isYearMode) {
      (props.onChange as (year: number, month: number) => void)(viewYear, selectedMonth);
    }
    handleClosePicker();
  }, [viewYear, props.onChange, isYearMode]);

  const handleYearNavPrev = () => {
    if (currentView === 'years') {
      setYearGroupStart((prev) => prev - 12);
    } else {
      setViewYear((prev) => prev - 1);
    }
  };

  const handleYearNavNext = () => {
    if (currentView === 'years') {
      setYearGroupStart((prev) => prev + 12);
    } else {
      setViewYear((prev) => prev + 1);
    }
  };

  const handleHeaderClick = () => {
    if (currentView === 'months' && !isYearMode) {
      setCurrentView('years');
    }
  };

  const headerTitle = useMemo(() => {
    if (currentView === 'years') {
      return `${yearGroupStart} — ${yearGroupStart + 11}`;
    }
    return `${viewYear}`;
  }, [currentView, yearGroupStart, viewYear]);

  const currentYearNow = new Date().getFullYear();
  const currentMonthNow = new Date().getMonth() + 1;

  const displayText = isYearMode
    ? `${year}`
    : `${MONTH_NAMES_FULL[month - 1]} ${year}`;

  const open = Boolean(anchorEl);

  const isPrevDisabled = isYearMode ? year <= minYear : disabled || !findAdjacentMonth(-1);
  const isNextDisabled = isYearMode ? year >= maxYear : disabled || !findAdjacentMonth(1);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton onClick={handlePrev} size="small" disabled={isPrevDisabled}>
        <ChevronLeft />
      </IconButton>

      <DateButton
        onClick={handleOpenPicker}
        disabled={disabled}
        startIcon={<CalendarIcon fontSize="small" />}
        sx={{ minWidth: isYearMode ? 100 : 150 }}
      >
        {displayText}
      </DateButton>

      <IconButton onClick={handleNext} size="small" disabled={isNextDisabled}>
        <ChevronRight />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: 8,
            },
          },
        }}
      >
        <Paper sx={{ width: 260, overflow: 'hidden' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <IconButton size="small" onClick={handleYearNavPrev}>
              <ChevronLeft fontSize="small" />
            </IconButton>

            <Button
              onClick={handleHeaderClick}
              disabled={currentView === 'years' || isYearMode}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {headerTitle}
            </Button>

            <IconButton size="small" onClick={handleYearNavNext}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          {currentView === 'years' ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 1 }}>
              {Array.from({ length: 12 }, (_, i) => yearGroupStart + i).map((y) => (
                <YearCell
                  key={y}
                  onClick={() => handleYearSelect(y)}
                  isSelected={year === y}
                  isCurrent={currentYearNow === y}
                  disabled={y < minYear || y > maxYear}
                >
                  {y}
                </YearCell>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 1 }}>
              {MONTH_NAMES_SHORT.map((name, idx) => {
                const m = idx + 1;
                return (
                  <MonthCell
                    key={m}
                    onClick={() => handleMonthSelect(m)}
                    isSelected={year === viewYear && month === m}
                    isCurrent={currentYearNow === viewYear && currentMonthNow === m}
                    disabled={isMonthDisabled?.(viewYear, m) ?? false}
                  >
                    {name}
                  </MonthCell>
                );
              })}
            </Box>
          )}
        </Paper>
      </Popover>
    </Box>
  );
}

export default MonthNavigator;
